import { z } from 'zod';
/**
 * Per `character-incident-monitor` change — single canonical record for
 * "this character's upstream is broken" surfaces. Written by Lambda
 * handlers on real upstream failures (5xx / timeout / ECONNREFUSED)
 * and surfaced both to:
 *
 *   - admin UI: 重大訊息 banner + /admin/incidents table, so operators
 *     know which of their characters needs IT attention
 *   - fan SPA: CharacterMarket banner swaps to the operator's
 *     maintenance image; CharacterChat blocks chat entry with a
 *     "正在維護中" overlay
 *
 * Dedup model: at most ONE open row per (characterId, kind). A second
 * failure in the same bucket bumps `lastSeenAt` + `occurrenceCount`
 * rather than creating a new row, so a flapping upstream doesn't
 * flood the admin UI with hundreds of red dots.
 *
 * Resolution model: manual only (operator decision). Auto-resolve
 * was considered + rejected — operators want to know about the
 * incident even after upstream recovers, to audit what happened.
 */
export const CharacterIncidentKindEnum = z.enum([
    'tts-upstream',
    'llm-upstream',
    'asr-upstream',
    'voice-upstream',
]);
export const CharacterIncidentStatusEnum = z.enum(['open', 'resolved']);
export const CharacterIncidentSchema = z.object({
    /** DDB partition key. */
    characterId: z.string().min(1),
    /** DDB sort key — `<epochMs>#<uuid>` for chronological ordering. */
    incidentId: z.string().min(1),
    kind: CharacterIncidentKindEnum,
    status: CharacterIncidentStatusEnum,
    /** Operator who owns the character; used for tenant-scoped queries. */
    customerId: z.string().min(1),
    /** ISO timestamp of the first observed failure in this dedup bucket. */
    firstSeenAt: z.string().datetime(),
    /** ISO timestamp of the most recent failure (refreshed on dedup). */
    lastSeenAt: z.string().datetime(),
    /** ISO timestamp the operator clicked 標為已處理. Absent while open. */
    resolvedAt: z.string().datetime().optional(),
    /** Cognito sub of the operator who resolved it (audit trail). */
    resolvedBy: z.string().optional(),
    /** How many failures have been bucketed into this incident. */
    occurrenceCount: z.number().int().positive(),
    /** Upstream host:port — useful for "is this the same ngrok URL" diagnosis. */
    upstreamHost: z.string().default(''),
    /** Coarse error code (`ECONNREFUSED` / `TIMEOUT` / `502` / `500`). */
    errorCode: z.string().default(''),
    /** Short 80-char message — first 80 chars of the raw error. */
    message: z.string().default(''),
});
